import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import AdminTerminologyModal from '../modals/AdminTerminologyModal'
import AdminTerminologyRevisionModal from '../modals/AdminTerminologyRevisionModal'

import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Pagination from '@mui/material/Pagination'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import CloseIcon from '@mui/icons-material/Close'
import ClearIcon from '@mui/icons-material/Clear'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import CheckIcon from '@mui/icons-material/Check'
import SearchIcon from '@mui/icons-material/Search'
import Grid from '@mui/material/Grid'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Tooltip from '@mui/material/Tooltip'
import Link from '@mui/material/Link'

import { AdminManage } from '../styles'

import { setErrorNotification } from '../../reducers/notificationReducer'
import { deleteTerminology, updateTerminology } from '../../reducers/terminologyReducer'
import { initializeRevisions, deleteRevision } from '../../reducers/revisionReducer'
import { initializeTags } from '../../reducers/tagsReducers'
import { fetchFlags, runScan, resolveFlag, dismissFlag } from '../../reducers/glossaryAutomationReducer'
import terminologyService from '../../services/terminology'
import { safeHref } from '../../utils/safeUrl'

const SORT_OPTIONS = [
    { value: '-created', label: 'Newest First' },
    { value: 'created', label: 'Oldest First' },
    { value: 'term', label: 'Term A-Z' },
    { value: '-term', label: 'Term Z-A' },
    { value: '-updated', label: 'Recently Updated' },
    { value: 'updated', label: 'Least Recently Updated' },
]

// Stable fallback so the tags selector doesn't return a new array on every store update.
const NO_TAGS = []

const APPROVED_TAB = 0
const PENDING_TAB = 1
const REVISIONS_TAB = 2
const FLAGGED_TAB = 3

// The queue opens on what still needs a verdict, so both the default list filter and
// the tab's count start from OPEN. While that filter is active, resolve/dismiss can
// splice the row out in place instead of forcing a refetch.
const OPEN_STATUS = 'OPEN'

const FLAG_KIND_LABELS = {
    broken_link: 'Broken link',
    stale: 'Stale term',
}

// Dismissing is permanent and there is no un-dismiss endpoint, so every status has to be
// reachable from the UI — otherwise a mis-click hides a flag with no way to even look at
// it again. '' means "send no filter", which the service and the slice both drop.
const FLAG_STATUS_OPTIONS = [
    { value: OPEN_STATUS, label: 'Open' },
    // Resolve sets FIXED (an admin dealt with it); RESOLVED is what a scan sets when the
    // problem has gone away on its own.
    { value: 'FIXED', label: 'Fixed' },
    { value: 'RESOLVED', label: 'Auto-resolved' },
    { value: 'DISMISSED', label: 'Dismissed' },
    { value: '', label: 'All statuses' },
]

// Without displayEmpty, MUI renders the '' ("All …") option as a blank field and leaves
// the label sitting over it — the shrink goes with it.
const FLAG_SELECT_PROPS = {
    SelectProps: { displayEmpty: true },
    InputLabelProps: { shrink: true },
}

const FLAG_KIND_OPTIONS = [
    { value: '', label: 'All types' },
    ...Object.entries(FLAG_KIND_LABELS).map(([value, label]) => ({ value, label })),
]

const TerminologyAdmin = () => {
    const [searchInput, setSearchInput] = useState('') // What user types
    const [searchQuery, setSearchQuery] = useState('') // Actual search query for API
    const [tabValue, setTabValue] = useState(APPROVED_TAB)
    const [page, setPage] = useState(1)
    const [flagPage, setFlagPage] = useState(1)
    const [ordering, setOrdering] = useState('-created')
    const [approvedCount, setApprovedCount] = useState(0)
    const [pendingCount, setPendingCount] = useState(0)
    const [revisionCount, setRevisionCount] = useState(0)
    const [approvedData, setApprovedData] = useState(null)
    const [pendingData, setPendingData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [flagStatus, setFlagStatus] = useState(OPEN_STATUS)
    const [flagKind, setFlagKind] = useState('')

    // The empty state has to say which of the two it is: an empty queue, or a filter that
    // matched nothing. Only the default view can promise a scan is what's missing.
    const flagFilterIsDefault = flagStatus === OPEN_STATUS && !flagKind

    // Revisions don't have an `updated` field, so hide those options on that tab
    const visibleSortOptions = tabValue === REVISIONS_TAB
        ? SORT_OPTIONS.filter(o => !o.value.endsWith('updated'))
        : SORT_OPTIONS

    const revisions = useSelector(state => state.revisions)
    const {
        flags = [],
        count: flagsCount = 0,
        totalPages: flagsTotalPages = 1,
        // Aliased: the component already owns a `loading` state for the terminology fetch.
        loading: flagsLoading = false,
        scanning = false,
        lastScan = null,
        error: flagsError = null,
    } = useSelector(state => state.glossaryAutomation) || {}
    const tags = useSelector(state => state.tags?.default || NO_TAGS)
    const user = useSelector(state => state.user)
    const isAdmin = user?.staff || user?.terminology_admin
    const dispatch = useDispatch()

    const optTags = tags.map(tag => ({ label: tag.name, value: tag.name }))

    // Load the tags needed by the term-editing modals. RequireStaff (App.js)
    // guarantees this component only mounts for a loaded, authorized
    // (staff or terminology_admin) user — this check just guards against
    // `isAdmin` being momentarily stale on the very first effect run.
    useEffect(() => {
        if (isAdmin) {
            dispatch(initializeTags())
        }
    }, [isAdmin, dispatch])

    // Fetch both approved and pending data together (no refetch on tab switch)
    useEffect(() => {
        if (isAdmin) {
            const fetchData = async () => {
                setLoading(true)
                try {
                    const [approvedResult, pendingResult] = await Promise.allSettled([
                        terminologyService.retrieveAllForAdmin(searchQuery, page, true, ordering),
                        terminologyService.retrieveAllForAdmin(searchQuery, page, false, ordering),
                    ])

                    let hasError = false

                    if (approvedResult.status === 'fulfilled') {
                        const approved = approvedResult.value
                        setApprovedData(approved)
                        setApprovedCount(approved.count || 0)
                    } else {
                        hasError = true
                        console.error('Failed to fetch approved terminology:', approvedResult.reason)
                    }

                    if (pendingResult.status === 'fulfilled') {
                        const pending = pendingResult.value
                        setPendingData(pending)
                        setPendingCount(pending.count || 0)
                    } else {
                        hasError = true
                        console.error('Failed to fetch pending terminology:', pendingResult.reason)
                    }

                    if (hasError) {
                        dispatch(setErrorNotification('Error retrieving terminology for admin'))
                    }
                } catch (error) {
                    console.error('Unexpected error while fetching terminology:', error)
                    dispatch(setErrorNotification('Error retrieving terminology for admin'))
                } finally {
                    setLoading(false)
                }
            }
            fetchData()
            // Revisions tab uses term/created only — coerce -updated/updated to -created
            const revisionOrdering = ordering.endsWith('updated') ? '-created' : ordering
            dispatch(initializeRevisions(page, revisionOrdering))
        }
    }, [searchQuery, page, ordering, dispatch, isAdmin])

    // Sync revision count from Redux state
    useEffect(() => {
        if (revisions.count !== undefined) {
            setRevisionCount(revisions.count)
        }
    }, [revisions.count])

    // Flags are fetched on mount too, not just when the tab is opened — the tab label
    // carries the flag count, so an admin has to be able to see there is work waiting
    // without clicking through to look. Off the flagged tab the requested page is pinned
    // to 1, so paging through terminology doesn't refetch flags. The flags pager has its
    // own page state (flagPage) so paging or filtering the Flagged tab doesn't also
    // re-run the terminology/revisions fetch effect, which keys off `page`.
    const flagsTabOpen = tabValue === FLAGGED_TAB
    const flagsPage = flagsTabOpen ? flagPage : 1

    // One builder for every flag request, so the list effect, the filter dropdowns and the
    // post-scan refetch can't drift apart. An unset filter is left off the request rather
    // than sent empty: the slice records the filters it actually sent, and that is what
    // decides whether an updated flag still belongs in the list.
    const buildFlagsQuery = useCallback(() => ({
        page: flagsPage,
        ...(flagStatus ? { status: flagStatus } : {}),
        ...(flagKind ? { kind: flagKind } : {}),
    }), [flagsPage, flagStatus, flagKind])

    const flagsFetchedRef = useRef(false)
    useEffect(() => {
        if (!isAdmin) return
        // Opening the tab re-reads the queue — what was fetched at mount can be stale by
        // the time the admin gets here. Leaving it doesn't: one fetch on mount is all the
        // tab count needs, so a refetch on the way out would be a wasted request.
        if (!flagsTabOpen && flagsFetchedRef.current) return
        flagsFetchedRef.current = true
        dispatch(fetchFlags(buildFlagsQuery()))
    }, [buildFlagsQuery, flagsTabOpen, dispatch, isAdmin])

    // Resolve and dismiss splice the row out without refetching, which can empty the page
    // being looked at. Stepping back re-fetches through flagsPage, rather than leaving the
    // admin staring at an empty table under a pager that says there is more. A failed
    // fetch also leaves the list empty, but stepping back there would just retry a
    // different page and read as a mystery second request.
    useEffect(() => {
        if (flagsTabOpen && !flagsLoading && !flagsError && flags.length === 0 && flagPage > 1) {
            setFlagPage(p => Math.max(1, p - 1))
        }
    }, [flagsTabOpen, flagsLoading, flagsError, flags.length, flagPage])

    // Helper to remove a term from a tab's cached data
    const removeTermFromTab = useCallback((termId, tab) => {
        const setter = tab === APPROVED_TAB ? setApprovedData : setPendingData
        setter(prev => {
            if (!prev) return prev
            const filtered = prev.results.filter(t => t.id !== termId)
            if (filtered.length === prev.results.length) return prev
            return { ...prev, results: filtered, count: prev.count - 1 }
        })
    }, [])

    // Helper to add a term to a tab's cached data
    const addTermToTab = useCallback((termData, tab) => {
        const setter = tab === APPROVED_TAB ? setApprovedData : setPendingData
        setter(prev => {
            if (!prev) return prev
            return { ...prev, results: [termData, ...prev.results], count: prev.count + 1 }
        })
    }, [])

    // Helper to update a term in a tab's cached data (e.g. after a revision is accepted)
    const updateTermInTab = useCallback((termId, updatedFields, tab) => {
        const setter = tab === APPROVED_TAB ? setApprovedData : setPendingData
        setter(prev => {
            if (!prev) return prev
            const updated = prev.results.map(t =>
                t.id === termId ? { ...t, ...updatedFields } : t
            )
            return { ...prev, results: updated }
        })
    }, [])

    // approve terminology (optimistic update — if the API call fails, a notification is shown but cache remains updated until next search/page change)
    const handleApprove = (term) => {
        if (window.confirm('Are you sure you want to approve this terminology?')) {
            const data = {
                ...term,
                approved: true
            }
            dispatch(updateTerminology(term.id, data, isAdmin))
            removeTermFromTab(term.id, PENDING_TAB)
            addTermToTab({ ...term, approved: true }, APPROVED_TAB)
            setPendingCount(c => c - 1)
            setApprovedCount(c => c + 1)
        }
    }

    // disapprove terminology (optimistic update — if the API call fails, a notification is shown but cache remains updated until next search/page change)
    const handleDisapprove = (term) => {
        if (window.confirm('Are you sure you want to unapprove this terminology?')) {
            const data = {
                ...term,
                approved: false
            }
            dispatch(updateTerminology(term.id, data, isAdmin))
            removeTermFromTab(term.id, APPROVED_TAB)
            addTermToTab({ ...term, approved: false }, PENDING_TAB)
            setApprovedCount(c => c - 1)
            setPendingCount(c => c + 1)
        }
    }

    // delete terminology
    const handleDelete = (term) => {
        if (window.confirm('Are you sure you want to delete this terminology? This action cannot be undone.')) {
            dispatch(deleteTerminology(term.id, isAdmin))
            removeTermFromTab(term.id, tabValue)
            if (tabValue === APPROVED_TAB) {
                setApprovedCount(c => c - 1)
            } else {
                setPendingCount(c => c - 1)
            }
        }
    }

    const handleDeleteRevision = (revision) => {
        if (window.confirm('Are you sure you want to delete this revision? This action cannot be undone.')) {
            dispatch(deleteRevision(revision.id, isAdmin))
            setRevisionCount(c => c - 1)
        }
    }

    // A scan can create, resolve, and suppress any number of rows at once, so there is
    // nothing sensible to patch in place — re-read the page once it finishes.
    const handleRunScan = async () => {
        await dispatch(runScan())
        dispatch(fetchFlags(buildFlagsQuery()))
    }

    // No refetch after these two: the thunks fold the server's updated flag back into the
    // list, splicing the row out when the new status no longer matches the active filter
    // (and leaving it in place when there is no status filter, e.g. "All statuses").
    const handleResolveFlag = (flag) => {
        dispatch(resolveFlag(flag.id))
    }

    const handleDismissFlag = (flag) => {
        dispatch(dismissFlag(flag.id))
    }

    // Page is reset here rather than in an effect on [flagStatus, flagKind]: an effect
    // would let the fetch effect fire once for the old page before the reset landed, and
    // the two responses could then race.
    const handleFlagFilterChange = (setFilter, value) => {
        setFilter(value)
        setFlagPage(1)
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue)
        setPage(1)
        // Leaving the Flagged tab pins its pager back to 1, so returning to it (or
        // switching away and letting the mount-only fetch stand) doesn't carry a stale page.
        setFlagPage(1)
        // Revisions tab doesn't support sorting by `updated` — fall back to default
        if (newValue === REVISIONS_TAB && ordering.endsWith('updated')) {
            setOrdering('-created')
        }
    }

    // Handle search input change (user typing)
    const handleSearchChange = (e) => {
        setSearchInput(e.target.value)
    }

    // Search and sort reset the pager in the same update as the change (like the
    // flag filters above), so the fetch effect runs once, for page 1.
    const changeSearchQuery = (query) => {
        if (query === searchQuery) return
        setSearchQuery(query)
        setPage(1)
    }

    // Handle search button click
    const handleSearchClick = () => {
        changeSearchQuery(searchInput)
    }

    // Handle Enter key press in search field
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearchClick()
        }
    }

    // Handle clear search
    const handleClearSearch = () => {
        setSearchInput('')
        changeSearchQuery('')
    }

    // Get terminology results from local cache (already filtered by approved status)
    const currentTabData = tabValue === APPROVED_TAB ? approvedData : pendingData
    const displayTerminology = currentTabData?.results || []
    const displayRevisions = revisions.results || []

    // Show loading state while data is being fetched
    if(loading && !approvedData && !pendingData) {
        return (
            <Container {...AdminManage.loading.props} sx={AdminManage.loading.style}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={60} />
                    <Typography variant="h5" gutterBottom>
                        Loading Terminology Management...
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Fetching terminology entries...
                    </Typography>
                </Box>
            </Container>
        )
    }

    return (
        <Container {...AdminManage.container.props} sx={AdminManage.container.style}>
            <Typography {...AdminManage.heading} gutterBottom>
                Glossary Administration
            </Typography>
            
            <Typography variant="body1" gutterBottom sx={{ marginBottom: 3 }}>
                Manage terminology entries, approve pending submissions, and maintain the glossary.
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                    <Tab label={`Approved (${approvedCount})`} />
                    <Tab label={`Pending Approval (${pendingCount})`} />
                    <Tab label={`Pending Revisions (${revisionCount})`} />
                    {/* The count is the server's count for the list as filtered, so it
                        keeps agreeing with the table. With the default OPEN filter that
                        is the open queue, which is what an admin looking at the tab from
                        another one needs to see. */}
                    <Tab label={`Flagged (${flagsCount})`} />
                </Tabs>
            </Box>

            {/* The flag list has no search or ordering support on the backend, so those
                controls are dropped on that tab rather than left there doing nothing —
                the same reduction the revisions tab makes to its sort options. What the
                backend does support is filtering by status and kind, so that is what the
                tab's toolbar offers instead. */}
            {tabValue === FLAGGED_TAB ?
                <Box sx={{ marginBottom: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: 2 }}>
                        <TextField
                            select
                            label="Status"
                            value={flagStatus}
                            onChange={(e) => handleFlagFilterChange(setFlagStatus, e.target.value)}
                            sx={{ minWidth: 200 }}
                            {...FLAG_SELECT_PROPS}
                        >
                            {FLAG_STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value || 'all'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Type"
                            value={flagKind}
                            onChange={(e) => handleFlagFilterChange(setFlagKind, e.target.value)}
                            sx={{ minWidth: 200 }}
                            {...FLAG_SELECT_PROPS}
                        >
                            {FLAG_KIND_OPTIONS.map((option) => (
                                <MenuItem key={option.value || 'all'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            onClick={handleRunScan}
                            disabled={scanning}
                        >
                            {scanning ? 'Scanning...' : 'Run scan'}
                        </Button>
                        {scanning && <CircularProgress size={24} aria-label="scan in progress" />}
                        <Typography variant="body2" color="textSecondary">
                            Checks the glossary for broken source links and terms that have gone stale.
                        </Typography>
                    </Box>
                    {lastScan && !scanning && (
                        <Typography variant="body2" sx={{ marginTop: 1 }}>
                            {lastScan.ran === false
                                ? 'Last scan: automation is disabled, so nothing was checked.'
                                : `Last scan: ${lastScan.created || 0} new flag(s), `
                                    + `${lastScan.resolved || 0} resolved, `
                                    + `${lastScan.suppressed || 0} suppressed.`}
                            {lastScan.skipped_checks?.length
                                ? ` Skipped: ${lastScan.skipped_checks.join(', ')}.`
                                : ''}
                        </Typography>
                    )}
                    {flagsError && (
                        <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
                            {flagsError}
                        </Typography>
                    )}
                </Box>
            :
            <Grid container spacing={3} sx={AdminManage.container.props}>
                <Grid item xs={12} md={9}>
                    <TextField
                        fullWidth
                        label="Search terminology..."
                        value={searchInput}
                        onChange={handleSearchChange}
                        onKeyPress={handleSearchKeyPress}
                        placeholder="Search by term, description, or tags... (Press Enter or click Search)"
                        sx={{ marginBottom: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchInput && (
                                        <IconButton
                                            onClick={handleClearSearch}
                                            edge="end"
                                            aria-label="clear search"
                                            size="small"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        onClick={handleSearchClick}
                                        edge="end"
                                        color="primary"
                                        aria-label="search"
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        select
                        label="Sort By"
                        value={ordering}
                        onChange={(e) => { setOrdering(e.target.value); setPage(1) }}
                        sx={{ marginBottom: 2 }}
                    >
                        {visibleSortOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>
            }

            {((tabValue === APPROVED_TAB || tabValue === PENDING_TAB) && displayTerminology && displayTerminology.length > 0) ?
                <>
                    <TableContainer component={Paper}>
                        <Table sx={AdminManage.table} aria-label="terminology table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={AdminManage.tableHead}>Term</TableCell>
                                    <TableCell {...AdminManage.centerAlign}>Description Preview</TableCell>
                                    <TableCell {...AdminManage.centerAlign}>Status</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Edit</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>
                                        {tabValue === APPROVED_TAB ? 'Unapprove' : 'Approve'}
                                    </TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Delete</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayTerminology.map(term => (
                                    <TableRow
                                        key={`terminology-${term.id}`}
                                        sx={AdminManage.tableRow}
                                    >
                                        <TableCell {...AdminManage.tableCell.props} sx={{...AdminManage.tableCell.style, maxWidth: '200px'}}>
                                            <Typography variant="h6" gutterBottom>
                                                {term.term}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Updated: {term.updated.split('T')[0]}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{maxWidth: '300px'}}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                            }}>
                                                {term.description.replace(/[#*`]/g, '').substring(0, 150)}...
                                            </Typography>
                                        </TableCell>
                                        <TableCell {...AdminManage.centerAlign}>
                                            <Typography color={term.approved ? 'success.main' : 'warning.main'}>
                                                {term.approved ? 'Approved' : 'Pending'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <AdminTerminologyModal
                                                {...term}
                                                optTags={optTags}
                                                onToggleApprove={(updatedTerm) => {
                                                    removeTermFromTab(term.id, tabValue)
                                                    const destTab = tabValue === APPROVED_TAB ? PENDING_TAB : APPROVED_TAB
                                                    addTermToTab(updatedTerm, destTab)
                                                    if (tabValue === APPROVED_TAB) {
                                                        setApprovedCount(c => c - 1)
                                                        setPendingCount(c => c + 1)
                                                    } else {
                                                        setPendingCount(c => c - 1)
                                                        setApprovedCount(c => c + 1)
                                                    }
                                                }}
                                                onDelete={() => {
                                                    removeTermFromTab(term.id, tabValue)
                                                    if (tabValue === APPROVED_TAB) {
                                                        setApprovedCount(c => c - 1)
                                                    } else {
                                                        setPendingCount(c => c - 1)
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            {tabValue === APPROVED_TAB ? (
                                                <Button {...AdminManage.reject.props} sx={AdminManage.reject.style} onClick={()=>handleDisapprove(term)}>
                                                    <CloseIcon />&nbsp;Unapprove
                                                </Button>
                                            ) : (
                                                <Button {...AdminManage.approve.props} sx={AdminManage.approve.style} onClick={()=>handleApprove(term)}>
                                                    <CheckIcon />&nbsp;Approve
                                                </Button>
                                            )}
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <Button {...AdminManage.reject.props} sx={AdminManage.reject.style} onClick={()=>handleDelete(term)}>
                                                <DeleteForeverIcon />&nbsp;Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        sx={AdminManage.pagination}
                        count={currentTabData?.total_pages || 1}
                        page={page}
                        onChange={(event, value) => setPage(value)}
                    />
                </>
            : (tabValue === REVISIONS_TAB && displayRevisions && displayRevisions.length > 0) ?
                <>
                    <TableContainer component={Paper}>
                        <Table sx={AdminManage.table} aria-label="terminology table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={AdminManage.tableHead}>Term</TableCell>
                                    <TableCell {...AdminManage.typeAlign}>Description Preview</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Review</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Delete</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayRevisions.map(revision => (
                                    <TableRow
                                        key={`revision-${revision.id}`}
                                        sx={AdminManage.tableRow}
                                    >
                                        <TableCell {...AdminManage.tableCell.props} sx={{...AdminManage.tableCell.style, maxWidth: '200px'}}>
                                            <Typography variant="h6" gutterBottom>
                                                {revision.term}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Submitted: {revision.created.split('T')[0]}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{maxWidth: '300px'}}>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                            }}>
                                                {revision.description.replace(/[#*`]/g, '').substring(0, 150)}...
                                            </Typography>
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <AdminTerminologyRevisionModal
                                                { ...revision }
                                                optTags={optTags}
                                                onRevisionHandled={(updatedTermData) => {
                                                    setRevisionCount(c => c - 1)
                                                    if (updatedTermData) {
                                                        // Revision was accepted — update the term in both caches
                                                        updateTermInTab(updatedTermData.id, updatedTermData, APPROVED_TAB)
                                                        updateTermInTab(updatedTermData.id, updatedTermData, PENDING_TAB)
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <Button {...AdminManage.reject.props} sx={AdminManage.reject.style} onClick={()=>handleDeleteRevision(revision)}>
                                                <DeleteForeverIcon />&nbsp;Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        sx={AdminManage.pagination}
                        count={revisions.total_pages || 1}
                        page={page}
                        onChange={(event, value) => setPage(value)}
                    />
                </>
            : (tabValue === FLAGGED_TAB && flags.length > 0) ?
                <>
                    <TableContainer component={Paper}>
                        <Table sx={AdminManage.table} aria-label="flagged terminology table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={AdminManage.tableHead}>Issue</TableCell>
                                    <TableCell {...AdminManage.typeAlign}>Subject</TableCell>
                                    <TableCell {...AdminManage.typeAlign}>Reason</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Resolve</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Dismiss</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {flags.map(flag => (
                                    <TableRow
                                        key={`flag-${flag.id}`}
                                        sx={AdminManage.tableRow}
                                    >
                                        <TableCell {...AdminManage.tableCell.props} sx={{...AdminManage.tableCell.style, maxWidth: '200px'}}>
                                            <Typography variant="h6" gutterBottom>
                                                {FLAG_KIND_LABELS[flag.kind] || flag.kind}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Flagged: {flag.created?.split('T')[0]}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{maxWidth: '300px', wordBreak: 'break-all'}}>
                                            {/* A broken_link flag is against the dead URL, which can be cited
                                                by several terms, so the URL is the subject there; a stale flag
                                                is against one term. */}
                                            {flag.source ? (
                                                safeHref(flag.source.url) ? (
                                                    <Link
                                                        href={safeHref(flag.source.url)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {flag.source.url}
                                                    </Link>
                                                ) : (
                                                    // A broken-link flag always has a URL (the flag is
                                                    // AGAINST that URL); show it as text when it isn't
                                                    // safe to link to (issue #404).
                                                    <Typography variant="body2">{flag.source.url || flag.source.source}</Typography>
                                                )
                                            ) : (
                                                <Typography variant="body2">{flag.term?.term || 'Unknown'}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{maxWidth: '300px'}}>
                                            <Typography variant="body2">
                                                {flag.reason}
                                            </Typography>
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <Tooltip title="Mark this flag as dealt with. A later scan can raise it again if the problem is still there." arrow>
                                                <span>
                                                    <Button {...AdminManage.approve.props} sx={AdminManage.approve.style} onClick={()=>handleResolveFlag(flag)}>
                                                        <CheckIcon />&nbsp;Resolve
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <Tooltip title="Permanently suppress this flag — the scan will never flag this subject again." arrow>
                                                <span>
                                                    <Button {...AdminManage.reject.props} sx={AdminManage.reject.style} onClick={()=>handleDismissFlag(flag)}>
                                                        <CloseIcon />&nbsp;Dismiss
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        sx={AdminManage.pagination}
                        count={flagsTotalPages}
                        page={flagPage}
                        onChange={(event, value) => setFlagPage(value)}
                    />
                </>
            : (tabValue === FLAGGED_TAB && flagsLoading) ?
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
                    <CircularProgress aria-label="loading flags" />
                </Box>
            :
                <Box sx={AdminManage.noResources}>
                    <QuestionMarkIcon sx={AdminManage.noResourceIcon} />
                    <Typography {...AdminManage.noResourceText} gutterBottom>
                        {tabValue === APPROVED_TAB ? 'No approved terminology found'
                            : tabValue === PENDING_TAB ? 'No pending terminology found'
                                : tabValue === REVISIONS_TAB ? 'No pending revisions found'
                                    : flagFilterIsDefault ? 'No open flags found'
                                        : 'No flags match these filters'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {tabValue === FLAGGED_TAB
                            ? (flagFilterIsDefault
                                ? 'Run a scan to check the glossary for broken source links and stale terms.'
                                : 'Try a different status or type.')
                            : searchQuery ? 'Try adjusting your search terms.' : 'Terminology entries will appear here once available.'}
                    </Typography>
                </Box>
            }
        </Container>
    )
}

export default TerminologyAdmin
